import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "../stores/auth";
import {
  ALL_ROLES,
  SCOUTING_ROLES,
  CUSTOM_REPORT_ROLES,
  PLAYFINDER_ROLES,
  hasAnyRole,
} from "../utils/roles.js";
import { safeNextPath } from "../utils/utils.js";

const LoginPage = () => import("../pages/LoginPage.vue");
const UnauthorizedPage = () => import("../pages/UnauthorizedPage.vue");
const LandingPage = () => import("../pages/LandingPage.vue");
const CoachingMinutesSheetPage = () =>
  import("../pages/coaching/MinutesSheetPage.vue");
const CoachingPlayFinderPage = () =>
  import("../pages/coaching/PlayFinderPage.vue");
const CustomReportPage = () => import("../pages/reports/CustomReportPage.vue");
const EtlReportPage = () => import("../pages/reports/etl/EtlReportPage.vue");
const ScoutingBookPage = () => import("../pages/scouting/ScoutingBookPage.vue");

const requireRoles = (allowedRoles) => async (to) => {
  const auth = useAuthStore();

  if (!auth.user) {
    await auth.fetchUser();
  }

  if (!auth.user) {
    // keep the deep link (path + query) through login
    return { path: "/", query: { next: to.fullPath } };
  }

  if (!hasAnyRole(auth.role, allowedRoles)) {
    return "/unauthorized";
  }

  return true;
};

// beforeEnter added for for dev bypass
const routes = [
  {
    path: "/",
    name: "Login",
    component: LoginPage,
    beforeEnter: async (to, from, next) => {
      const auth = useAuthStore();
      if (!auth.user) await auth.fetchUser();
      if (auth.user) {
        next(safeNextPath(to.query.next) || "/landing");
      } else {
        next();
      }
    },
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: UnauthorizedPage,
  },
  {
    path: "/landing",
    component: LandingPage,
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/coaching/minutes-sheet",
    component: CoachingMinutesSheetPage,
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/coaching/play-finder",
    component: CoachingPlayFinderPage,
    beforeEnter: requireRoles(PLAYFINDER_ROLES),
  },
  {
    path: "/reports/custom",
    component: CustomReportPage,
    beforeEnter: requireRoles(CUSTOM_REPORT_ROLES),
  },
  {
    path: "/reports/postgame",
    component: EtlReportPage,
    props: { report: "postgame" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/3t",
    component: EtlReportPage,
    props: { report: "3t" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/season",
    component: EtlReportPage,
    props: { report: "season" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/extended-matchups",
    component: EtlReportPage,
    props: { report: "extended_matchups" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/vs-opponent",
    component: EtlReportPage,
    props: { report: "vs_opponent" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/minutes-sheet",
    component: EtlReportPage,
    props: { report: "minutes_sheet" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/l5-games",
    component: EtlReportPage,
    props: { report: "l5_games" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/reports/l10-games",
    component: EtlReportPage,
    props: { report: "l10_games" },
    beforeEnter: requireRoles(ALL_ROLES),
  },
  {
    path: "/scouting/draft-books",
    component: ScoutingBookPage,
    props: { book: "draft-books" },
    beforeEnter: requireRoles(SCOUTING_ROLES),
  },
  {
    path: "/scouting/free-agent-books",
    component: ScoutingBookPage,
    props: { book: "free-agent-books" },
    beforeEnter: requireRoles(SCOUTING_ROLES),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/landing",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Warm every page's chunk once the first page is up
const PAGES = [
  LandingPage,
  CoachingMinutesSheetPage,
  CoachingPlayFinderPage,
  CustomReportPage,
  EtlReportPage,
  ScoutingBookPage,
];

router.isReady().then(
  () => {
    const whenIdle =
      window.requestIdleCallback ?? ((cb) => setTimeout(cb, 200));
    whenIdle(() => PAGES.forEach((load) => load().catch(() => {})));
  },
  () => {}
);

export default router;
